/** Spreadsheet-style row letters: A..Z, then AA, AB, … for very long coaches. */
export function rowLabel(row) {
    let label = '';
    let n = row;
    do {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return label;
}
/** Default seat name for a grid position: row letter + 1-based seat number. */
export function autoSeatCode(row, seatIndexInRow) {
    return `${rowLabel(row)}${seatIndexInRow + 1}`;
}
/**
 * Builds a rectangular layout. Aisles, doors and the driver do not consume seat
 * numbers, so seat names stay contiguous (A1, A2 across an aisle, not A1, A3).
 */
export function buildGridLayout(rows, columns, options = {}) {
    const aisles = new Set(options.aisleColumns ?? []);
    const doors = new Set((options.doors ?? []).map(([r, c]) => `${r}:${c}`));
    const driver = options.driver ? `${options.driver[0]}:${options.driver[1]}` : null;
    const cells = [];
    for (let row = 0; row < rows; row++) {
        let seatIndex = 0;
        for (let col = 0; col < columns; col++) {
            const key = `${row}:${col}`;
            let type = 'SEAT';
            if (driver === key)
                type = 'DRIVER';
            else if (doors.has(key))
                type = 'DOOR';
            else if (aisles.has(col))
                type = 'AISLE';
            cells.push({
                code: type === 'SEAT' ? autoSeatCode(row, seatIndex++) : '',
                row,
                col,
                type,
            });
        }
    }
    return cells;
}
export function layoutCapacity(seats) {
    return seats.reduce((total, cell) => total + (cell.type === 'SEAT' ? 1 : 0), 0);
}
/** Bookable seat codes, in reading order (top-left to bottom-right). */
export function seatCodes(seats) {
    return [...seats]
        .filter((cell) => cell.type === 'SEAT' && cell.code)
        .sort((a, b) => a.row - b.row || a.col - b.col)
        .map((cell) => cell.code);
}
/**
 * Structural checks run before a layout is stored. A malformed layout would
 * silently break seat selection and the manifest, so this is a save-time gate.
 */
export function validateSeatLayout(layout) {
    const errors = [];
    const { rows, columns, seats } = layout;
    if (!Number.isInteger(rows) || rows < 1)
        errors.push('Rows must be a positive whole number.');
    if (!Number.isInteger(columns) || columns < 1) {
        errors.push('Columns must be a positive whole number.');
    }
    const positions = new Set();
    const codes = new Set();
    for (const cell of seats) {
        if (cell.row < 0 || cell.row >= rows || cell.col < 0 || cell.col >= columns) {
            errors.push(`Cell ${cell.code || `${cell.row},${cell.col}`} is outside the ${rows}×${columns} grid.`);
            continue;
        }
        const position = `${cell.row}:${cell.col}`;
        if (positions.has(position)) {
            errors.push(`Two cells share the position row ${cell.row + 1}, column ${cell.col + 1}.`);
        }
        positions.add(position);
        if (cell.type === 'SEAT') {
            if (!cell.code.trim()) {
                errors.push(`A seat at row ${cell.row + 1}, column ${cell.col + 1} has no code.`);
            }
            else if (codes.has(cell.code)) {
                errors.push(`Seat code ${cell.code} is used more than once.`);
            }
            else {
                codes.add(cell.code);
            }
        }
    }
    if (layoutCapacity(seats) === 0) {
        errors.push('A layout needs at least one seat.');
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=seatmap.js.map